/* eslint-disable react/no-clone-element */
/* eslint-disable react/no-unstable-default-props */
import { matchSorter } from "match-sorter";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
// import clsx from 'clsx';
import {
  useAsyncDebounce,
  useExpanded,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

import { useUIStateStore } from "@/store/uiStateStore";

import useWindowDimensions from "../helpers/useWindowDimensions";
// import useGetColours from '../helpers/getColours';
import Input from "./Input";
import TablePagination from "./TablePagination";

function GlobalFilter({
  area,
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  searchSuffix,
  total,
  paginated,
}) {
  const count = paginated ? total : preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((v) => {
    setGlobalFilter(v || undefined);
  }, 500);

  // Sync local state with external globalFilter prop (e.g., when reset on account switch)
  React.useEffect(() => {
    setValue(globalFilter || "");
  }, [globalFilter]);

  const { t } = useTranslation();
  // const { highlightColour2 } = useGetColours();

  // const [isHovered, setIsHovered] = useState(false);
  // const [isFocused, setIsFocused] = useState(false);

  const isTunecore = useUIStateStore(state => state.isTunecore);
  // const searchBarClasses = clsx(
  //   'rounded-lg p-2 w-full pl-10 bg-layer-raised border-0 h-full focus:outline-none focus:ring-2',
  //   {
  //     'text-black border-0 focus:ring-2': !isTunecore,
  //     'bg-layer-inverse text-white border-none focus:ring-action-primary': isTunecore,
  //   },
  // );
  return (
    <div
      className={`${
        isTunecore ? "tunecore-filter" : "input-grey-bg"
      } relative w-full`}
    >
      <Input
        searchIcon
        id={`txtTableSearch${area}`}
        name={`txtTableSearch${area}`}
        value={value}
        placeholder={`${t("common:filter")} ${count} ${searchSuffix}`}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        onBlur={() => onChange(value)}
        type="search"
        className="w-full"
        required={false}
        showLabel
        error={null}
      />
    </div>
  );
}

// Define a default UI for filtering
function DefaultColumnFilter() {
  return null;
}

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [row => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = val => !val;

function Table({
  area = "",
  columns,
  data,
  revealComponent,
  highlightColour,
  searchSuffix,
  disableSearch = false,
  title = null,
  bgClass = "",
  min = 10,
  noneMessage = null,
  getRowId = null,
  disablePagination = false,
  onRowClick = () => {},
  paginated = false,
  setTableState = () => {},
  tableState,
  total = 0,
  initialSort,
  currentpage,
  filter = () => {},
  setFilter = () => {},
  filtersSection,
  fadingOutRow, // Add fadingOutRow prop
  disableRowHover = false, // Add disableRowHover prop
}) {
  const { t } = useTranslation();

  const isTunecore = useUIStateStore(state => state.isTunecore);
  const filterTypes = () => ({
    // Add a new fuzzyTextFilterFn filter type.
    fuzzyText: fuzzyTextFilterFn,
    // Or, override the default text filter to use
    // "startWith"
    text: (rows, id, filterValue) =>
      rows.filter((row) => {
        const rowValue = row.values[id];
        return rowValue !== undefined
          ? String(rowValue)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase())
          : true;
      }),
  });

  const defaultColumn = () => ({
    // Let's set up our default Filter UI
    Filter: DefaultColumnFilter,
  });

  const nextPageState = () => {
    const p = tableState.page;
    setTableState("page", p + 1);
  };

  const setPageSizeState = (size) => {
    setTableState("perPage", size);
  };

  const prevPageState = () => {
    const p = tableState.page;
    setTableState("page", p - 1);
  };

  const getRowProps = (row) => {
    // Only apply the fading-out class if fadingOutRow is defined and matches the row's workcode
    if (fadingOutRow && row.original.workcode === fadingOutRow) {
      return {
        className: "opacity-0 transition-opacity duration-700 ease-out",
      };
    }
    return {}; // Return an empty object for other rows
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    preGlobalFilteredRows,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter, sortBy },
  } = useTable(
    {
      columns,
      data,
      getRowId: React.useCallback((row, index) => getRowId ? getRowId(row, index) : index, [getRowId]),
      autoResetPage: false,
      autoResetExpanded: false,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      initialState: {
        pageIndex: currentpage ? currentpage - 1 : 0, // Convert from 1-based to 0-based indexing
        pageSize: min,
        sortBy: useMemo(
          () =>
            initialSort || [
              {
                id: "title",
                desc: false,
              },
            ],
          [initialSort],
        ),
        hiddenColumns: ["colGigId", "colSetlistId"],
      },
      manualPagination: paginated,
      manualSortBy: paginated,
      getRowProps, // Add the getRowProps function HERE!

    },
    useFilters, // useFilters!
    useGlobalFilter, // useGlobalFilter!
    useSortBy,
    useExpanded,
    usePagination,
  );

  const { width } = useWindowDimensions();
  useEffect(() => {
    if (paginated) {
      setTableState("sortBy", sortBy);
    }
  }, [sortBy, setTableState, paginated]);

  // useEffect(() => {
  //   if (sortBy !== initialSort) {
  //     onSort(sortBy);
  //     setTableState('page', 1);
  //   }
  // }, [sortBy]);

  return (
    <div>
      {!disableSearch && (
        <div className="flex flex-col gap-4 mb-5 md:flex-row">
          <GlobalFilter
            area={area}
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={paginated ? filter : globalFilter}
            setGlobalFilter={paginated ? setFilter : setGlobalFilter}
            searchSuffix={searchSuffix}
            total={total}
            paginated={paginated}
          />
          {filtersSection && (
            <div className="flex flex-row gap-2 min-w-72 md:max-w-1/2">
              {filtersSection}
            </div>
          )}
        </div>
      )}
      {!!title && (
        <div className="flex items-center w-full mb-4 lg:w-1/2">
          <h2
            className={`${
              isTunecore ? "text-white" : "text-ink-primary"
            } font-semibold text-lg`}
          >
            {title}
          </h2>
          <div
            className={`ml-2 mr-2 inline-flex h-5 items-center px-1 text-xxs rounded uppercase ${
              isTunecore
                ? "bg-white/25 text-white"
                : "bg-old-song-sub-color/25 text-old-song-sub-color"
            }`}
          >
            {data?.length || 0}
          </div>
        </div>
      )}

      <div
        className={`${
          isTunecore ? "text-white" : "text-ink-primary"
        } ${bgClass}`}
      >
        {width > 600 && (
          <table
            className={`${
              isTunecore
                ? "divide-y divide-tunecore-black"
                : "divide-y divide-gray-200"
            } min-w-full text-sm`}
            {...getTableProps()}
          >
            <thead>
              {headerGroups.map((headerGroup) => {
                const headerGroupProps = headerGroup.getHeaderGroupProps();
                const { key: headerGroupKey, ...otherHeaderGroupProps } = headerGroupProps;

                return (
                  <tr key={headerGroupKey} {...otherHeaderGroupProps}>
                    {headerGroup.headers.map((column) => {
                      const headerProps = column.getHeaderProps({
                        ...column.getSortByToggleProps(),
                        style: {
                          minWidth: column.width,
                          width: column.width,
                          maxWidth: column.width,
                        },
                        id: column.id,
                      });
                      const { key, ...otherHeaderProps } = headerProps;

                      return (
                        <th
                          key={key}
                          className={`p-2 text-left cursor-pointer ${
                            isTunecore
                              ? "text-white"
                              : "text-sm text-ink-secondary"
                          } font-semibold uppercase tracking-wider `}
                          {...otherHeaderProps}
                        >
                          <span className={`${column.canSort ? "underline" : ""}`}>
                            {column.render("Header")}
                          </span>
                          <span>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? <FaChevronDown className="inline-block ml-1" />
                                : <FaChevronUp className="inline-block ml-1" />
                              : ""}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody
              className={`${
                isTunecore
                  ? "bg-transparent divide-y divide-tunecore-black"
                  : "bg-white divide-y divide-old-sentric-border-gray"
              } w-full font-semibold`}
              {...getTableBodyProps()}
            >
              {page.length > 0
                && page.map((row) => {
                  prepareRow(row);
                  const rowProps = row.getRowProps(getRowProps(row));
                  const { key: rowKey, ...otherRowProps } = rowProps;

                  return (
                    <React.Fragment key={row.id || row.original.id || row.index}>
                      <tr
                        key={rowKey}
                        onClick={() => onRowClick(row)}
                        {...(revealComponent ? row.getToggleRowExpandedProps() : {})}
                        className={`${disableRowHover ? "" : "cursor-pointer"} ${
                          disableRowHover
                            ? ""
                            : isTunecore
                              ? "hover:bg-canvas-inverse"
                              : "hover:bg-layer-raised"
                        }`}
                        {...otherRowProps}
                        id={`btn${area}Row`}
                      >
                        {row.cells.map((cell) => {
                          const cellProps = cell.getCellProps();
                          const { key: cellKey, ...otherCellProps } = cellProps;

                          return (
                            <td
                              key={cellKey}
                              className="px-2 py-4 pl-2 pr-0 whitespace-wrap"
                              {...otherCellProps}
                            >
                              <div className="truncate whitespace-normal">
                                {cell.render("Cell")}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                      {row.isExpanded && revealComponent && (
                        <>
                          {React.cloneElement(revealComponent, { row })}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              {page.length === 0 && (
                <tr>
                  <td colSpan="2">
                    <div className="w-full p-4">
                      {!noneMessage && (
                        <p>
                          {t("common:table-there-are-no")}
                          {" "}
                          {searchSuffix}
                          .

                        </p>
                      )}
                      {!!noneMessage && <p>{noneMessage}</p>}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {width < 600 && (
          <table className="w-full text-sm divide-y divide-gray-200">
            <tbody
              className={`${
                isTunecore
                  ? "bg-transparent divide-y divide-tunecore-black"
                  : "bg-white divide-y divide-old-sentric-border-gray"
              } flex flex-wrap w-full font-semibold `}
              {...getTableBodyProps()}
            >
              {page.length > 0 && page.map((row) => {
                prepareRow(row);
                return (
                  <React.Fragment key={row.id || row.original.id || row.index}>
                    <tr
                      onClick={() => onRowClick(row)}
                      {...(revealComponent ? row.getToggleRowExpandedProps() : {})}
                      className={`${disableRowHover ? "" : "cursor-pointer"} ${
                        disableRowHover
                          ? ""
                          : isTunecore && revealComponent
                            ? "hover:bg-canvas-inverse"
                            : revealComponent
                              ? "hover:bg-layer-disabled"
                              : ""
                      } w-full flex`}
                      {...row.getRowProps(getRowProps(row))}
                    >
                      <td colSpan="10" className="relative flex flex-wrap w-full my-3">
                        {row.cells.map((cell) => {
                          const headerContent
                            = typeof cell.column.Header === "function"
                              ? cell.column.Header()
                              : cell.column.Header;
                          return (
                            <div
                              key={cell.column.id}
                              className="w-full p-1 my-1"
                            >
                              {cell.column.id !== "expander" && (
                                <div className="flex flex-row justify-between w-full">
                                  <div
                                    className={`${
                                      isTunecore
                                        ? "text-white"
                                        : "text-ink-primary"
                                    } sm:w-3/12 w-5/12 text-xxs font-semibold uppercase tracking-wider`}
                                  >
                                    {headerContent}
                                  </div>
                                  <div className="flex w-6/12 truncate whitespace-normal">
                                    {cell.render("Cell")}
                                  </div>
                                </div>
                              )}
                              {cell.column.id === "expander" && (
                                <div className="absolute top-2 right-4">
                                  {cell.render("Cell")}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </td>
                    </tr>
                    {row.isExpanded && revealComponent && (
                      <>
                        {React.cloneElement(revealComponent, { row })}
                      </>

                    )}
                  </React.Fragment>
                );
              })}
              {page.length === 0 && (
                <tr className="w-full">
                  <td colSpan="10">
                    <div className="w-full p-4">
                      {!noneMessage && (
                        <p>
                          {t("common:table-there-are-no")}
                          {" "}
                          {searchSuffix}
                          .
                        </p>
                      )}
                      {!!noneMessage && <p>{noneMessage}</p>}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      {!disablePagination && (
        <div className={`${bgClass} pt-4 pb-4`}>
          <TablePagination
            previousPage={() =>
              paginated ? prevPageState() : previousPage()}
            canPreviousPage={
              paginated ? tableState.page !== 1 : canPreviousPage
            }
            nextPage={() => (paginated ? nextPageState() : nextPage())}
            canNextPage={
              paginated
                ? tableState.page !== Math.ceil(total / tableState.perPage)
                : canNextPage
            }
            pageSize={paginated ? tableState.perPage : pageSize}
            setPageSize={size =>
              paginated ? setPageSizeState(size) : setPageSize(size)}
            pageIndex={paginated ? tableState.page - 1 : pageIndex}
            totalPages={
              paginated
                ? Math.ceil(total / tableState.perPage)
                : pageOptions.length
            }
            highlightColour={highlightColour}
            area={area}
          />
        </div>
      )}
    </div>
  );
}

export default Table;
